import type { BillingMode, BillingTier, GraphQLTypes, InputType, OrganizationStatus } from '@zeus/index'
import { $, Selector } from '@zeus/index'
import { typedGql } from '@zeus/typedDocumentNode'

const orgSelector = Selector('OrganizationModel')({
  id: true,
  name: true,
  slug: true,
  status: true,
  userCount: true,
  tier: true,
  billingMode: true,
  billingNote: true,
})

export const GQL_ORGANIZATIONS = typedGql('query')({
  organizations: orgSelector,
})

export const GQL_CREATE_ORGANIZATION = typedGql('mutation')({
  createOrganization: [
    { input: $('input', 'CreateOrganizationInput!') },
    orgSelector,
  ],
})

export const GQL_SET_ORGANIZATION_STATUS = typedGql('mutation')({
  setOrganizationStatus: [
    { id: $('id', 'String!'), status: $('status', 'OrganizationStatus!') },
    orgSelector,
  ],
})

export const GQL_UPDATE_ORGANIZATION = typedGql('mutation')({
  updateOrganization: [
    { input: $('input', 'UpdateOrganizationInput!') },
    orgSelector,
  ],
})

/** Le SuperAdmin fixe la formule tant que l'achat en ligne est coupé (#165). */
export const GQL_SET_ORGANIZATION_PLAN = typedGql('mutation')({
  setOrganizationPlan: [
    { orgId: $('orgId', 'ID!'), input: $('input', 'ChangeBillingPlanInput!') },
    { id: true, tier: true },
  ],
})

/** Compte géré par Studeva, hors abonnement (#349) : le super-admin le pose ou le retire. */
export const GQL_SET_ORGANIZATION_BILLING_MODE = typedGql('mutation')({
  setOrganizationBillingMode: [
    { orgId: $('orgId', 'ID!'), input: $('input', 'SetBillingModeInput!') },
    { id: true, tier: true, mode: true },
  ],
})

export const GQL_DELETE_ORGANIZATION = typedGql('mutation')({
  deleteOrganization: [{ id: $('id', 'ID!') }, true],
})

const memberSelector = Selector('OrgMemberModel')({
  id: true,
  createdAt: true,
  email: true,
  firstName: true,
  lastName: true,
  role: true,
  status: true,
})

export const GQL_ORGANIZATION_MEMBERS = typedGql('query')({
  organizationMembers: [{ orgId: $('orgId', 'ID!') }, memberSelector],
})

export const GQL_INVITE_ORG_MEMBER = typedGql('mutation')({
  inviteOrgMember: [{ input: $('input', 'InviteOrgMemberInput!') }, memberSelector],
})

/**
 * Renvoie le membre à jour plutôt qu'un booléen : la ligne se remplace sur
 * place, sans relire toute la liste pour un seul changement de rôle.
 */
export const GQL_SET_ORG_MEMBER_ROLE = typedGql('mutation')({
  setOrgMemberRole: [{ input: $('input', 'SetOrgMemberRoleInput!') }, memberSelector],
})

export const GQL_REMOVE_ORG_MEMBER = typedGql('mutation')({
  removeOrgMember: [
    { orgId: $('orgId', 'ID!'), userId: $('userId', 'ID!') },
    true,
  ],
})

export type StoredOrganization = InputType<
  GraphQLTypes['OrganizationModel'],
  typeof orgSelector
>

export type StoredOrgMember = InputType<
  GraphQLTypes['OrgMemberModel'],
  typeof memberSelector
>

// Types concrets pour le rendu : les `InputType` zeus résolvent leurs champs en
// `unknown` (inexploitables en accès de champ), d'où ces formes explicites.
export type OrgRow = {
  id: string
  name: string
  slug: string
  status: OrganizationStatus
  userCount: number
  tier: BillingTier
  billingMode: BillingMode
  billingNote?: string | null
}

export type OrgMember = {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  status: string
}
